import React, { useState } from 'react';
import { setState as setGlobalState } from '../lib/userStore';

const CARRIERS = [
  { value: 'bcbs',     label: 'BlueCross BlueShield of SC' },
  { value: 'aetna',    label: 'Aetna' },
  { value: 'cigna',    label: 'Cigna' },
  { value: 'united',   label: 'UnitedHealthcare' },
  { value: 'humana',   label: 'Humana' },
  { value: 'medicaid', label: 'SC Medicaid' },
  { value: 'medicare', label: 'Medicare' },
];

const SEX_OPTIONS = [
  { value: 'female',     label: 'Female' },
  { value: 'male',       label: 'Male' },
  { value: 'prefer_not', label: 'Prefer not to say' },
];

function ageFromDob(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today < new Date(today.getFullYear(), birth.getMonth(), birth.getDate())) age--;
  return age > 0 ? String(age) : null;
}

const TOTAL_STEPS = 4;

export default function OnboardingScreen({ saveProfile }) {
  const [step,     setStep]     = useState(0);
  const [firstName, setFirstName] = useState('');
  const [lastName,  setLastName]  = useState('');
  const [zipCode,   setZipCode]   = useState('');
  const [carrier,   setCarrier]   = useState('');
  const [planType,  setPlanType]  = useState('');
  const [dob,       setDob]       = useState('');
  const [sex,       setSex]       = useState('');
  const [saving,    setSaving]    = useState(false);

  const canAdvance = [
    firstName.trim().length > 0 && lastName.trim().length > 0,
    /^\d{5}$/.test(zipCode.trim()),
    !carrier || !!planType,   // either nothing selected (skip) or both selected
    true,                     // optional step — always advanceable
  ][step];

  async function handleNext() {
    if (step < TOTAL_STEPS - 1) {
      setStep(s => s + 1);
      return;
    }
    setSaving(true);
    const age = ageFromDob(dob);
    const updates = {
      firstName: firstName.trim(),
      lastName:  lastName.trim(),
      zipCode:   zipCode.trim(),
      dob:       dob || null,
      age,
      gender:    sex || null,
      ...(carrier && planType ? { carrier, planType } : {}),
    };
    setGlobalState(updates);
    await saveProfile(updates);
  }

  function handleSkipInsurance() {
    setCarrier('');
    setPlanType('');
    setStep(s => s + 1);
  }

  return (
    <div style={s.screen}>
      <div style={s.accentBar} />

      {/* Wordmark */}
      <div style={s.wordmarkRow}>
        <span style={s.wordmarkBold}>Clear</span>
        <span style={s.wordmarkItalic}>path</span>
      </div>

      {/* Step dots */}
      <div style={s.dots}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div key={i} style={{ ...s.dot, background: i === step ? 'var(--teal)' : i < step ? 'var(--teal-2)' : 'var(--border)' }} />
        ))}
      </div>

      {/* Question area */}
      <div style={s.questionArea}>

        {/* Step 0 — Name */}
        {step === 0 && (
          <>
            <div style={s.hint}>Let's get you set up</div>
            <div style={s.question}>What's your<br /><em style={{ color: 'var(--teal)', fontStyle: 'italic' }}>name?</em></div>
            <div style={s.sub}>We'll use it to personalize your experience.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input
                autoFocus
                style={s.input}
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && lastName.trim() && canAdvance && handleNext()}
                placeholder="First name"
                maxLength={40}
              />
              <input
                style={s.input}
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && canAdvance && handleNext()}
                placeholder="Last name"
                maxLength={40}
              />
            </div>
          </>
        )}

        {/* Step 1 — Zip code */}
        {step === 1 && (
          <>
            <div style={s.hint}>Nice to meet you, {firstName.trim()}.</div>
            <div style={s.question}>What's your<br /><em style={{ color: 'var(--teal)', fontStyle: 'italic' }}>zip code?</em></div>
            <div style={s.sub}>We use this to find providers near you.</div>
            <input
              autoFocus
              style={s.input}
              type="tel"
              inputMode="numeric"
              maxLength={5}
              value={zipCode}
              onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
              onKeyDown={e => e.key === 'Enter' && canAdvance && handleNext()}
              placeholder="e.g. 29601"
            />
          </>
        )}

        {/* Step 2 — Insurance (skippable) */}
        {step === 2 && (
          <>
            <div style={s.hint}>Almost there.</div>
            <div style={s.question}>Who's your<br /><em style={{ color: 'var(--amber)', fontStyle: 'italic' }}>insurance?</em></div>
            <div style={s.sub}>Helps us show which providers accept your plan.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={s.selectWrap}>
                <select
                  style={{ ...s.input, ...s.select, color: carrier ? 'var(--ink)' : 'var(--ink-4)' }}
                  value={carrier}
                  onChange={e => { setCarrier(e.target.value); setPlanType(''); }}
                >
                  <option value="">Select your carrier…</option>
                  {CARRIERS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              {carrier && (
                <div style={s.selectWrap}>
                  <select
                    style={{ ...s.input, ...s.select, color: planType ? 'var(--ink)' : 'var(--ink-4)' }}
                    value={planType}
                    onChange={e => setPlanType(e.target.value)}
                    autoFocus
                  >
                    <option value="">Select plan type…</option>
                    <option value="ppo">PPO</option>
                    <option value="hmo">HMO</option>
                    <option value="hdhp">High-Deductible (HDHP)</option>
                    <option value="epo">EPO</option>
                  </select>
                </div>
              )}
            </div>
          </>
        )}

        {/* Step 3 — Optional details */}
        {step === 3 && (
          <>
            <div style={s.hint}>
              One more thing
              <span style={s.optionalBadge}>Optional</span>
            </div>
            <div style={s.question}>A few personal<br /><em style={{ color: 'var(--teal)', fontStyle: 'italic' }}>details</em></div>
            <div style={s.sub}>You can skip this and add it anytime from your Profile.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={s.fieldLabel}>Date of birth</div>
                <input
                  style={{ ...s.input, color: dob ? 'var(--ink)' : 'var(--ink-4)' }}
                  type="date"
                  value={dob}
                  onChange={e => setDob(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={s.fieldLabel}>Biological sex</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {SEX_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      style={{
                        ...s.sexBtn,
                        background: sex === opt.value ? 'var(--teal)' : 'var(--surface)',
                        color:      sex === opt.value ? 'white'       : 'var(--ink-2)',
                        border:     `1.5px solid ${sex === opt.value ? 'var(--teal)' : 'var(--border-strong)'}`,
                      }}
                      onClick={() => setSex(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

      </div>

      {/* Footer */}
      <div style={s.footer}>
        <button
          style={{ ...s.continueBtn, opacity: canAdvance && !saving ? 1 : 0.4 }}
          disabled={!canAdvance || saving}
          onClick={handleNext}
        >
          {saving ? 'Saving…' : step === TOTAL_STEPS - 1 ? 'Done →' : 'Continue →'}
        </button>

        {step === 2 && (
          <button style={s.skipBtn} onClick={handleSkipInsurance}>
            Skip insurance for now
          </button>
        )}

        {step === 3 && (
          <button style={s.skipBtn} onClick={handleNext} disabled={saving}>
            Skip and finish
          </button>
        )}
      </div>
    </div>
  );
}

const s = {
  screen:         { flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--cream)', padding: '0 28px', position: 'relative', overflowY: 'auto' },
  accentBar:      { position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, var(--teal), var(--teal-2))' },
  wordmarkRow:    { display: 'flex', alignItems: 'baseline', gap: '2px', marginTop: '56px', marginBottom: '28px', flexShrink: 0 },
  wordmarkBold:   { fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: '18px', color: 'var(--teal)', letterSpacing: '-0.5px' },
  wordmarkItalic: { fontFamily: 'var(--font-display)', fontWeight: 300, fontStyle: 'italic', fontSize: '18px', color: 'var(--ink-3)', letterSpacing: '-0.3px' },
  dots:           { display: 'flex', gap: '8px', marginBottom: '36px', flexShrink: 0 },
  dot:            { width: '8px', height: '8px', borderRadius: '50%', transition: 'background 0.3s' },
  questionArea:   { flex: 1, display: 'flex', flexDirection: 'column' },
  hint:           { fontSize: '13px', fontWeight: 500, color: 'var(--ink-4)', letterSpacing: '0.02em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' },
  optionalBadge:  { fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', background: 'var(--cream-2)', color: 'var(--ink-4)', padding: '2px 8px', borderRadius: '100px', border: '1px solid var(--border)' },
  question:       { fontFamily: 'var(--font-display)', fontWeight: 400, fontSize: '32px', color: 'var(--ink)', letterSpacing: '-0.5px', lineHeight: 1.2, marginBottom: '10px' },
  sub:            { fontSize: '13.5px', color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: '24px' },
  fieldLabel:     { fontSize: '11.5px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-3)' },
  input:          { width: '100%', padding: '15px 18px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--border-strong)', background: 'var(--surface)', fontFamily: 'var(--font-body)', fontSize: '16px', color: 'var(--ink)', outline: 'none', boxSizing: 'border-box' },
  selectWrap:     { position: 'relative' },
  select:         { appearance: 'none', fontSize: '15px', cursor: 'pointer' },
  sexBtn:         { width: '100%', padding: '14px 18px', borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 500, cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' },
  footer:         { paddingBottom: '40px', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0 },
  continueBtn:    { width: '100%', padding: '16px 20px', borderRadius: 'var(--radius-md)', border: 'none', background: 'var(--teal)', color: 'white', fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 600, cursor: 'pointer', transition: 'opacity 0.2s' },
  skipBtn:        { background: 'none', border: 'none', fontSize: '13px', color: 'var(--ink-4)', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'center', padding: '4px' },
};
